// Content for Śrīmad-Bhāgavatam 1.5.17 commentary topic

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.5.17',
        sloka: `tyaktvā sva-dharmaṁ caraṇāmbujaṁ harer
bhajann apakvo 'tha patet tato yadi |
yatra kva vābhadram abhūd amuṣya kiṁ
ko vārtha āpto 'bhajatāṁ sva-dharmataḥ ||`,
        translation:
            'If a person having given up his duties in varṇāśrama (<strong>tyaktvā sva-dharmaṁ</strong>), begins the worship of the Lord’s lotus feet (<strong>caraṇāmbujaṁ harer bhajan</strong>), and happens to deviate or not reach perfection (<strong>apakvo ’tha patet tato yadi</strong>), there is no misfortune for him at all in the future (<strong>yatra kva vābhadram abhūd amuṣya kiṁ</strong>). But what does the person who follows all duties of varṇāśrama but does not worship the Lord gain (<strong>ko vārtha āpto ’bhajatāṁ sva-dharmataḥ</strong>)?',
    },
    blocks: [
        {
            type: 'dialogue',
            objection: '"But Bhagavad-Gītā says:"',
            response:
                'That is true. This instruction is given to one who is teaching jñāna to another person. Practicing jñāna depends on inner purification, and that purification depends on niṣkāma-karma. <strong>This is not an instruction for one teaching another person about bhakti, for bhakti is independently powerful, not depending on prior inner purification.</strong> Lord Ajita says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 3.26',
                sloka: `na buddhi-bhedaṁ janayed
ajñānāṁ karma-saṅginām |
yojayet sarva-karmāṇi
vidvān yuktaḥ samācaran ||`,
                translation:
                    'The wise man should not disturb the intelligence of the ignorant attached to results of work. Engaging in all work himself with detachment, he should engage them in activity.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 6.9.50',
                sloka: `svayaṁ niḥśreyasaṁ vidvān
na vakty ajñāya karma hi
na rāti rogiṇo 'pathyaṁ
vāñchato 'pi bhiṣaktamaḥ`,
                translation:
                    'One who knows the highest good will never instruct a foolish person to engage in material enjoyment, just as an experienced physician gives his patient eat food injurious to his health, even if the patient desires it.',
            },
        },
        {
            type: 'text',
            content: 'Therefore the Lord says <em>sarva-dharmān parityajya mām ekaṁ śaraṇaṁ vraja</em> (Bhagavad-Gītā 18.66) and as well:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.11.32',
                sloka: `dharmān santyajya yaḥ sarvān māṁ bhajeta sa tu sattamaḥ`,
                translation:
                    'Having taken complete shelter at My lotus feet, however, a saintly person ultimately renounces such ordinary religious duties and worships Me alone. He is thus considered to be the best among all living entities.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>From the strength of these statements one should teach only bhakti to the Lord, while giving up attachment to daily and periodic duties in varṇāśrama.</strong> With this intention the verse is spoken.',
        },
        {
            type: 'text',
            content:
                'Even in the beginning stage of bhakti, practice of karma is forbidden. <strong>Having given up ones dharma (<em>tyaktvā svadharmān</em>) even at the beginning stages of bhakti, a person who worships the lotus feet of the Lord will incur no sin by giving up that dharma.</strong> This is understood also from the following:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.5.41',
                sloka: `devarṣi-bhūtāpta-nṛṇāṁ pitṛṇāṁ
na kiṅkaro nāyam ṛṇī ca rājan
sarvātmanā yaḥ śaraṇaṁ śaraṇyaṁ
gato mukundaṁ parihṛtya kartam`,
                translation:
                    'Anyone who has taken shelter at the lotus feet of Mukunda, the giver of liberation, giving up all kinds of obligation, and has taken to the path in all seriousness, owes neither duties nor obligations to the demigods, sages, general living entities, family members, humankind or forefathers.',
            },
        },
        {
            type: 'text',
            content:
                'And if the person dies without being qualified to attain the Lord, or (<em>vā</em>), if while alive falls down because of sinful acts, being attached to material pleasures,<br /><br /><strong>even then, there is no misfortune incurred by having given up karmas, because the impressions created by bhakti are indestructible, and remain in subtle form. In such a state one should not revert to performance of karma.</strong>',
        },
        {
            type: 'text',
            content: '<strong>Is there any misfortune somewhere (<em>yatra kva</em>), in his next life?</strong>',
        },
        {
            type: 'text',
            content: '<strong>No there is no misfortune at all. <em>Vā</em> indicates a remote possibility of falling.</strong>',
        },
        {
            type: 'text',
            content:
                'It is stated as a concession to the opponent (<em>tuṣyatu durjana nyāya</em>). His falling after leaving the present body is actually not a fall—one does not take a low body because of omitting karmas.',
        },
        {
            type: 'text',
            content: 'This is shown by the following statement of the Lord:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.29.20',
                sloka: `na hy aṅgopakrame dhvaṁso
mad-dharmasyoddhavāṇv api
mayā vyavasitaḥ samyaṅ
nirguṇatvād anāśiṣaḥ`,
                translation:
                    'O Uddhava! Because I have personally established it, this process of niṣkāma-bhakti is beyond the guṇas.\nEven by starting and not completing the process, there is no destruction of results.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>This is because certainly the sprout of bhakti, being productive, will eventually bear leaves, flowers and fruit.</strong><br /><br />Instead of the present or future verb, the past tense is used (<em>abhūt</em>). This implies a criticism of all those who object (by showing that the fact is already proven).<br /><br /><strong>And what is gained from performing all of one\'s karmas (<em>sva-dharmataḥ</em>) by persons who do not worship the Lord (<em>abhajatām</em>).</strong>',
        },
    ],
};

export default content;
